/**
=========================================================
* Soft UI Dashboard PRO React - v4.0.0
=========================================================

* Product Page: https://www.creative-tim.com/product/soft-ui-dashboard-pro-react
* Copyright 2022 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

import React, { useEffect, useState } from "react";

// prop-types is a library for typechecking of props
import PropTypes from "prop-types";

// @mui material components
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Tooltip, { TooltipProps, tooltipClasses } from "@mui/material/Tooltip";
import { styled } from "@mui/system";

import Icon from "@mui/material/Icon";
import LoopIcon from "@mui/icons-material/Loop";
import Accordion from "@mui/material/Accordion";
import AccordionSummary from "@mui/material/AccordionSummary";
import AccordionDetails from "@mui/material/AccordionDetails";
import Typography from "@mui/material/Typography";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";

// Soft UI Dashboard PRO React components
import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import SoftButton from "components/SoftButton";

// Settings page components
import SoftInput from "components/SoftInput";

// react-router components

import { getUserData } from "Utils/helpers";
import { useSoftUIController } from "context";

/**
 *
 */
const HtmlTooltip = styled(({ className, ...props }: TooltipProps) => (
  <Tooltip {...props} classes={{ popper: className }} />
))(({ theme }) => ({
  [`& .${tooltipClasses.tooltip}`]: {
    //backgroundColor: "#f5f5f9",
    backgroundColor: "#000",
    color: "rgba(f, f, f, 1)", //"rgba(0, 0, 0, 0.87)",
    placement: "left",
    minWidth: "minContent",
    height: "minContent",
    textAlign: "left",
    fontSize: "0.65erm",
    border: "1px solid #dadde9",
    boxShadow: "0rem 1.25rem 1.6875rem 0rem rgba(0, 0, 0, 0.05)",
    padding: "15px",
    marginLeft: "15px",
  },
}));

function AdvancedSettlementFilters({
  applyFilters,
  settlementOutcomes,
  informParent,
  forceToggle,
  loadingData,
}) {
  const [controller, dispatch] = useSoftUIController();
  const { providers } = controller;

  const [filters, setFilters] = useState({});
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [outcomes, setOutcomes] = useState([]);
  const [providerSelected, setProviderSelected] = useState(null);

  const [applyOn, setApplyOn] = useState(false);
  const [loading, setLoading] = useState(false);

  const userData = getUserData();
  const platforms = userData
    ? userData.platforms
      ? userData.platforms.map((platform) => ({
          value: platform.id,
          label: platform.name,
          logo: platform.logo,
          sorting: platform.sorting,
          odds_styles: platform.odds_styles,
        }))
      : []
    : [];

  const handleSelectionValueChange = (item, type) => {
    let filtersToSet = { ...filters };

    if (type == "odduuid") {
      item.value = item.currentTarget.value;
    }

    filtersToSet[type] = item.value;
    setFilters(filtersToSet);
  };

  const checkApplyDisable = () => {
    let disabled = true;

    if (!Object.keys(filters).length > 0) {
      disabled = true;
    } else {
      for (const key in filters) {
        if (Object.hasOwnProperty.call(filters, key)) {
          const filter = filters[key];
          if (typeof filter == "number") {
            if (filter != -100) {
              disabled = false;
              break;
            }
          }
          if (typeof filter == "string") {
            if (filter != "") {
              disabled = false;
              break;
            }
          }
          if (typeof filter == "object") {
            if (filter.length > 0) {
              disabled = false;
              break;
            }
          }
        }
      }
    }

    return disabled;
  };

  const handleApply = () => {
    setApplyOn(true);
    let filtersToSend = {
      uuid: filters["odduuid"],
    };

    if (informParent && Object.keys(filters).length > 0) informParent(filtersToSend, false);
  };

  const handleReset = () => {
    document.getElementById("odduuid").value = null;
    setProviderSelected(null);

    setFilters([]);
  };

  const handleAccordion = () => {
    setFiltersOpen((prev) => !prev);
  };

  useEffect(() => {
    setFiltersOpen(forceToggle);
    setApplyOn(false);
    //console.log("forceToogle: ", forceToggle);
  }, [forceToggle]);

  useEffect(() => {
    if (settlementOutcomes) setOutcomes(settlementOutcomes);
  }, [settlementOutcomes]);

  useEffect(() => {
    if (informParent && applyFilters != undefined && Object.keys(filters).length > 0) {
      let filtersToSend = {
        uuid: filters["odduuid"],
      };
      informParent(filtersToSend, true);
    }
  }, [applyFilters]);

  useEffect(() => {
    //console.log("loading...", loading, loadingData);
    setLoading(loadingData);
  }, [loadingData]);

  return (
    <SoftBox mb={3}>
      <Grid container spacing={0}>
        <Card style={{ width: "100%", overflow: "visible" }}>
          <Grid item xs={12} sx={{ paddingTop: "0px", height: "100%" }}>
            <div>
              <Accordion
                expanded={filtersOpen}
                onChange={handleAccordion}
                sx={{ boxShadow: "none", borderRadius: "20px !important" }}
              >
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  aria-controls="settlement-filters-content"
                  id="settlement-filters-header"
                >
                  <Typography variant="h5">Filters</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Grid container spacing={3} className="bet-filter-group">
                    <Grid item xs={2} md={2}>
                      <SoftBox mb={1} ml={0.5} lineHeight={0} display="inline-block">
                        <SoftTypography
                          component="label"
                          variant="caption"
                          fontWeight="bold"
                          textTransform="capitalize"
                        >
                          Odd uuid:
                        </SoftTypography>
                        <HtmlTooltip
                          placement="top"
                          title={
                            <React.Fragment>
                              <div style={{ display: "flex", flexWrap: "wrap" }}>
                                This is the uuid of the odd.
                              </div>
                            </React.Fragment>
                          }
                        >
                          <Icon className="info-icon-filters" title="">
                            info_outlined
                          </Icon>
                        </HtmlTooltip>
                      </SoftBox>
                      <SoftInput
                        id="odduuid"
                        size="small"
                        onChange={(item) => handleSelectionValueChange(item, "odduuid")}
                      />
                    </Grid>
                  </Grid>
                  <Grid my={2}>
                    <hr style={{ color: "rgb(236, 236, 236)", border: "0.5px solid" }} />
                  </Grid>
                  <Grid container spacing={1} display="flex" alignItems="flex-end">
                    <Grid item style={{ width: "85%" }}></Grid>
                    <Grid item>
                      <SoftButton color="light" onClick={handleReset}>
                        Reset
                      </SoftButton>
                    </Grid>
                    <Grid item>
                      <SoftButton color="dark" onClick={handleApply} disabled={checkApplyDisable()}>
                        Apply
                        <LoopIcon
                          style={{ display: loading && applyOn ? "" : "none" }}
                          sx={{
                            animation: "spin 2s linear infinite",
                            "@keyframes spin": {
                              "0%": {
                                transform: "rotate(360deg)",
                              },
                              "100%": {
                                transform: "rotate(0deg)",
                              },
                            },
                          }}
                        />
                      </SoftButton>
                    </Grid>
                  </Grid>
                </AccordionDetails>
              </Accordion>
            </div>
          </Grid>
        </Card>
      </Grid>
    </SoftBox>
  );
}

AdvancedSettlementFilters.propTypes = {
  applyFilters: PropTypes.bool,
  settlementOutcomes: PropTypes.array,
  informParent: PropTypes.func,
  forceToggle: PropTypes.bool,
  loadingData: PropTypes.bool,
};

export default AdvancedSettlementFilters;
